import { InfoCircleOutlined } from "@ant-design/icons";
import { Empty, Modal, Spin, Tabs, Tag } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useState } from "react";
import { useQuery } from "react-query";
import { serverUrl } from "../../nestserver";

const READ_BLUE = "#34B7F1";
const SENT_GREY = "#8c8c8c";

/**
 * Single tick = sent, grey double = read by some, blue double = read by all.
 * Renders nothing when the server did not send receipt counts (the viewer is
 * not allowed to see them).
 */
export const ReadTicks = ({ readCount, recipientCount }) => {
  if (recipientCount === undefined || recipientCount === null) return null;
  const allRead = recipientCount > 0 && readCount >= recipientCount;
  const someRead = readCount > 0;
  return (
    <span
      title={
        recipientCount > 0
          ? `Read by ${readCount} of ${recipientCount}`
          : "No recipients"
      }
      style={{
        color: allRead ? READ_BLUE : SENT_GREY,
        fontSize: 14,
        letterSpacing: -4,
        fontWeight: 700,
      }}
    >
      {someRead ? "✓✓" : "✓"}
    </span>
  );
};

const ReceiptList = ({ people, emptyText, showTime }) => {
  if (!people?.length) return <Empty description={emptyText} />;
  return (
    <div style={{ maxHeight: 360, overflowY: "auto" }}>
      {people.map((person) => (
        <div
          key={person.user_id}
          className="flex items-center justify-between"
          style={{ padding: "8px 0", borderBottom: "1px solid #f0f0f0" }}
        >
          <div>
            <div style={{ fontWeight: 500 }}>{person.name}</div>
            <div style={{ fontSize: 12, color: SENT_GREY }}>
              <Tag style={{ marginRight: 6 }}>{person.role}</Tag>
              {person.student_name ? `of ${person.student_name}` : null}
            </div>
          </div>
          {showTime && person.read_at ? (
            <span style={{ fontSize: 12, color: SENT_GREY }}>
              {moment(person.read_at).format("DD-MM-YYYY hh:mm A")}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
};

/**
 * Ticks plus an (i) button opening the Read / Not read list.
 * `apiPrefix` is "/conversations" (senior) or "/v2/conversations" (junior).
 * Pass `readInfoPath` (e.g. "/personal-messages/:id/read-info") for other
 * message types; it overrides `apiPrefix`.
 */
const MessageReadStatus = ({
  message,
  apiPrefix = "/conversations",
  readInfoPath,
}) => {
  const [open, setOpen] = useState(false);
  const messageId = message?._id;
  const path = readInfoPath || `${apiPrefix}/messages/${messageId}/read-info`;

  const { data, isLoading, isError } = useQuery(
    [path, "read-info"],
    async () => {
      const res = await axios({
        method: "GET",
        url: `${serverUrl}${path}`,
        headers: {
          Authorization: `Bearer ${localStorage["jwt-token"]}`,
        },
      });
      return res.data.result;
    },
    { enabled: open && !!messageId, refetchOnWindowFocus: false },
  );

  if (
    message?.recipient_count === undefined ||
    message?.recipient_count === null
  )
    return null;

  return (
    <>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          lineHeight: 1,
        }}
      >
        <ReadTicks
          readCount={message.read_count || 0}
          recipientCount={message.recipient_count}
        />
        <InfoCircleOutlined
          className="cursor-pointer"
          style={{ color: SENT_GREY }}
          title="Message info"
          onClick={() => setOpen(true)}
        />
      </span>
      <Modal
        open={open}
        title="Message info"
        footer={null}
        onCancel={() => setOpen(false)}
        centered
      >
        {isLoading ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <Spin />
          </div>
        ) : isError || !data ? (
          <Empty description="Could not load read info" />
        ) : (
          <>
            {data.legacy_unavailable && (
              <div style={{ color: SENT_GREY, marginBottom: 8 }}>
                This message was sent before read tracking was added, so
                individual readers are not available.
              </div>
            )}
            <Tabs
              items={[
                {
                  key: "read",
                  label: `Read (${data.read.length})`,
                  children: (
                    <ReceiptList
                      people={data.read}
                      emptyText="No one has read this yet"
                      showTime
                    />
                  ),
                },
                {
                  key: "unread",
                  label: `Not read (${data.unread.length})`,
                  children: (
                    <ReceiptList
                      people={data.unread}
                      emptyText="Everyone has read this"
                    />
                  ),
                },
              ]}
            />
          </>
        )}
      </Modal>
    </>
  );
};

export default MessageReadStatus;
